import {Injectable, NgZone,} from '@angular/core';
import {Router,} from '@angular/router';
import * as printJS from 'print-js';
import {Decimal,} from 'decimal.js';
import {environment,} from '@environments/environment';
import {
  SystemConstant,
  JsonUtil,
  Logger,
  MessageStackService,
  ScreenDefinitionProvider, ThrownErrorHandler,
  WindowStack,
  FileUtil,
  ChangeTypeUtil,
} from '@services/index';
import {ErrorListOnTransitionVO, MessageLevel, ScreenVO,} from '@models/index';
import {AbstractControl, FormArray, FormControl, FormGroup,} from '@angular/forms';
import {MatCheckbox,} from '@angular/material/checkbox';
import {ViewPdfWindowInVO,} from '@pages/common-component/view-pdf-window/view-pdf-window.component-in-vo';
import {SortEvent,} from 'primeng/api';
import * as moment from 'moment';

/**
 * Windowサービス.
 */
@Injectable({
  providedIn: 'root',
})
export class WindowService {
  private readonly BLANK = 'about:blank';
  // _blankの文字列定数
  private readonly TARGET_BLANK = '_blank';
  // 画面定義情報
  screenInfo?: ScreenVO;
  // モバイルかPCかを画面幅を元にリアルタイムで判定した結果をtrue/falseで持つ。
  isMobile = true;
  // フォームに変更が加えられていないかをチェックするための事前登録されたフォーム群(遷移前にチェックが必要なフォームのみ)
  forms: (FormGroup | FormControl)[] = [];

  // 親画面
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parentScreen?: any;

  // 画面が openChild() メソッドを使用して呼び出された場合、true に設定される。
  hasParentScreen?: boolean;

  // 画面が openChild() メソッドを使用して呼び出され、コールバック関数がある場合は 親画面のscreenId に設定する。
  callbackParentScreenId?: string;

  // 子画面/親画面のマップ変数
  childParentMap: Map<string, string> = new Map<string, string>();

  // [QA:#59941] 以下の画面IDは子画面判定の対象外となる。
  skippedScreenIds = ['ESB03001', 'CSI01002', 'ESE02024', 'ESE02026', 'ESE02012',];

  // 画面間トランスアクションキー
  transactionKey?: string | null = null;

  // 画面間トランスアクション有無フラグ
  dbTransactionFlag = false;

  // 非同期トランザクション有無フラグ
  asyncTransactionFlag = false;

  constructor(
    private ngZone: NgZone,
    private constant: SystemConstant,
    private screenDefinitionProvider: ScreenDefinitionProvider,
    private router: Router,
    private logger: Logger,
    private windowStack: WindowStack,
    private thrownErrorHandler: ThrownErrorHandler,
    private jsonUtil: JsonUtil,
    private fileUtil: FileUtil
  ) {
    this.handleResizeWindow(window.innerWidth);
    // リサイズされる度にモバイルかどうかを判定する
    window.onresize = () => {
      ngZone.run(() => {
        this.handleResizeWindow(window.innerWidth);
      });
    };
  }

  /**
   * [一般開発者利用不可]
   * モバイルかどうかを判断する
   * @param width Windowサイズ
   * @private
   */
  private handleResizeWindow(width: number): void {
    if (width < this.constant.SCREEN_TYPE_RECOGNITION_WIDTH) {
      this.isMobile = true;
    } else {
      this.isMobile = false;
    }
  }

  /**
   * [一般開発者利用不可]
   * 画面情報を設定する。画面IDを受け取って、app-routing.moduleから情報を取得する
   * app.tsにてrouting時に自動セットするようにしているため、開発者の実装は不要。
   * @param screenId 画面機能ID
   */
  setScreenInfo(screenId: string): void {
    this.screenInfo = this.screenDefinitionProvider.getScreenDefinition(screenId);
  }

  /**
   * [一般開発者利用不可]
   * 画面遷移時等当該画面情報をクリアしたい場合にコールする
   * @private
   */
  private clearScreenInfo(): void {
    // ストレージやWindowStackから当該画面の情報を消去する
    if(this.screenInfo?.screenId){
      this.removeItemFromLocalStorage(this.screenInfo?.screenId);
      this.removeItemFromSessionStorage(this.screenInfo?.screenId);
    }
    // 画面定義情報をクリアする
    this.screenInfo = void 0;
    // フォーム情報をクリアする
    this.forms = [];
  }

  /**
   * [一般開発者利用可]
   * 自画面遷移する
   * @param screenId 画面機能ID
   * @param params 遷移先に渡すパラメータ(Local Storage経由)
   */
  transit<T>(screenId: string, params: T | null = null): void {
    this.logger.debug('自画面遷移します。画面ID:' + screenId + '|遷移パラメータ:', params);
    // 画面IDから画面定義を取得
    const nextScreenDefinition = this.screenDefinitionProvider.getScreenDefinition(screenId);
    // 画面定義が取得できなければシステムエラーをスローする
    if (!nextScreenDefinition || !nextScreenDefinition.path) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      this.logger.error('CSS01999M039I', screenId);
      messageStack.add({messageId: 'CSS01999M001E',});
      throw messageStack;
    } else {
      /**
       * 現画面に対する処理
       */
      // 画面遷移前に現在の画面情報をクリアする。
      this.clearScreenInfo();
      /**
       * 次画面に対する処理
       */
      // 次画面に遷移するパラメータをセットする
      if (params) {
        this.setItemToLocalStorage<T>(nextScreenDefinition.screenId, params);
      }
      // 画面遷移
      this.router.routeReuseStrategy.shouldReuseRoute = function () {
        return false;
      };
      this.router.navigate(['/' + nextScreenDefinition.path,]);
    }
  }

  /**
   * [一般開発者利用可]
   * 親画面のインスタンスを設定する。
   * @param parentScreen 親画面
   */
  // eslint-disable-next-line
  setParentScreen(parentScreen: any) : void {
    this.parentScreen = parentScreen;
  }

  /**
   * [一般開発者利用可]
   * 子ウィンドウオープン時処理.
   * @param screenId 画面機能ID
   * @param params 遷移先に渡すパラメータ(Local Storage経由)
   * @param callBackWhenClosing 子画面Close時のコールバック(子画面側のOutVOの型を引数にしたコールバック)
   * @param callBackWhenError 子画面でエラーが発生したときの時のコールバック
   */
  openChild<IN,OUT>(
    screenId: string,
    params: IN | null = null,
    callBackWhenClosing?: ((outParams: OUT) => void)| null,
    callBackWhenError?: ((outParams: ErrorListOnTransitionVO) => void) | null
  ): Window {
    this.logger.debug('子画面を開きます。画面ID:' + screenId + '|遷移パラメータ:', params);

    // 画面IDから画面定義を取得
    const screenDefinition = this.screenDefinitionProvider.getScreenDefinition(screenId);
    // 画面定義が取得できなければシステムエラーをスローする
    if (!screenDefinition) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      this.logger.error('CSS01999M039I', screenId);
      messageStack.add({messageId: 'CSS01999M001E',});
      throw messageStack;
    } else if (!screenDefinition.path) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      this.logger.error('CSS01999M039I', screenId);
      messageStack.add({messageId: 'CSS01999M001E',});
      throw messageStack;
    } else {
      // 次画面に遷移するパラメータをセットする
      this.setItemToLocalStorage<IN>(screenDefinition.screenId, params);
      this.logger.debug('Open window parameters:', this.getWindowParameters(screenDefinition));
      const windowKey = screenId + new Date().getTime();
      const openedWindow = window.open(screenDefinition.path, windowKey, this.getWindowParameters(screenDefinition));
      // 画面が起動できないときはポップアップブロックの設定があるので、エラーを表示する
      if(!openedWindow){
        const messageStack = new MessageStackService(MessageLevel.ERROR);
        messageStack.add({messageId: 'CSS01999M013E',});
        throw messageStack;
      }

      if (callBackWhenClosing) {
        this.childParentMap.set((<Window>openedWindow).name, this.screenInfo?.screenId || '');
        (<Window>openedWindow).localStorage.setItem((<Window>openedWindow).name + 'callback-parent', this.screenInfo?.screenId || '');
      }
      (<Window>openedWindow).localStorage.setItem((<Window>openedWindow).name + 'isChildScreen', 'true');

      this.windowStack.add(windowKey,<Window>openedWindow);
      // イベントメッセージを登録し、開発者が業務ロジックによりいつでも親画面のcallBack()を呼び出せるようにする。
      (<Window>openedWindow).addEventListener('message',
        (event: MessageEvent) => {
          this.ngZone.run(() => {
            try {
              this.windowStack.add(windowKey,<Window>openedWindow);

              if (this.parentScreen && event.data.type === 'callMethodFromParent') {
                // 親画面にメソッドが存在するかどうかを確認する。
                if (typeof this.parentScreen[event.data.methodName] === 'function') {
                  // 親画面でメソッドを呼び出す。
                  const childScreenDefinition = this.screenDefinitionProvider.getScreenDefinition(event.data.childScreenId);
                  if (event.data.hasParameter) {
                    this.parentScreen[event.data.methodName](JSON.parse(event.data.parameter), childScreenDefinition);
                  } else {
                    this.parentScreen[event.data.methodName](childScreenDefinition);
                  }
                } else {
                  (<Window>openedWindow).postMessage({type: 'callMethodFromParentError',}, location.origin);
                }
              }
            } catch (error) {
              (<Window>openedWindow).postMessage({type: 'callMethodFromParentError',}, location.origin);
            }
          });
      });

      // 画面クローズ時のイベントを定義しておく
      (<Window>openedWindow).addEventListener(
        'unload',
        () => {
          // 子画面が開かれると、ブラウザは1回目アンロードイベントを実行する。
          // ユーザーがブラウザの閉じるボタンを押下すると、ブラウザは2回目アンロードイベントを実行する。
          // 1回目アンロードイベントを実行するときにコールバックと項目削除を回避するために、アンロードイベントに処理を再割り当てる必要がある。
          (<Window>openedWindow).addEventListener(
            'unload',
            () => {
              this.ngZone.run(() => {
                this.windowStack.get().delete(windowKey);

                const outParams = this.getItemFromLocalStorage<OUT | ErrorListOnTransitionVO>(screenId);
                try {
                  this.logger.debug('outParams:', outParams);
                  // 返却値がエラー情報の場合
                  if(outParams && 'errorListOnTransition' in outParams) {
                    this.logger.debug('子画面でエラーが発生。');
                    // エラー用のコールバックがある場合は、エラー情報を設定してそのCallBack関数を呼び出す
                    if (callBackWhenError) {
                      this.logger.debug('親画面のエラー時のコールバックが設定されていたためコールします。');
                      callBackWhenError(<ErrorListOnTransitionVO>outParams);
                    // エラー用のコールバックがない場合は、フレームワーク側でエラーをスローする
                    } else {
                      this.logger.debug('親画面のエラー時のコールバックが設定されていないため、エラーをthrowします');
                      throw <ErrorListOnTransitionVO>outParams;
                    }
                  // 子画面側でセットした返却パラメータを取り出し、そのパラメータをCallBack関数に設定してCallBack関数を呼び出す
                  } else {
                    if (callBackWhenClosing) {
                      const origParent = this.childParentMap.get((<Window>openedWindow).name);
                      this.childParentMap.delete((<Window>openedWindow).name);

                      // 親画面が閉じていない場合にのみコールバックを実行します。
                      if (origParent != null && origParent === this.screenInfo?.screenId) {
                        this.logger.debug('callBackWhenClosingを実行する。' + origParent + ' ' + this.screenInfo?.screenId);
                        callBackWhenClosing(<OUT>outParams);
                      } else {
                        this.logger.debug('callBackWhenClosingを実行しない。' + origParent + ' ' + this.screenInfo?.screenId);
                      }
                    }
                  }
                } catch (error) {
                  this.logger.error('CSS01999M036E');
                  this.thrownErrorHandler.handleError(error);
                } finally {
                  this.removeItemFromLocalStorage(screenId);
                  this.removeItemFromSessionStorage(screenId);
                }
              });
            });
        });
      return <Window>openedWindow;
    }
  }

  /**
   * [一般開発者利用可]
   * 別Windowで直接PDFファイルを開く
   * @param filePath PDFファイルのパス
   */
  openPdfFileInChildWindow(filePath: string): void {
    const openedWindow = window.open(filePath, this.TARGET_BLANK, this.getPdfWindowParameters());
    // 画面が起動できないときはポップアップブロックの設定があるので、エラーを表示する
    if(!openedWindow){
      const messageStack = new MessageStackService(MessageLevel.ERROR);
  //     messageStack.add({messageId: 'CSS01999M013E',});
      throw messageStack;
    }


    (<Window>openedWindow).localStorage.setItem((<Window>openedWindow).name + 'isChildScreen', 'true');
  }

  /**
   * [一般開発者利用可]
   * 対象 base64 文字列のメディア タイプに従って、適切なメソッドを呼び出す。
   * @param base64Pdf base64のstring
   * @param filename ファイル名
   */
    openBase64PdfInChildWindow(base64Pdf: string, filename?: string): void {
    if (this.isMediaTypePdf(base64Pdf)) {
      this.openMediaTypePdfInChildWindow(base64Pdf, filename);
    } else {
      this.openBase64OtherTypeInChildWindow(base64Pdf, filename);
    }
  }

    /**
   * [一般開発者利用可]
   * ChromeのブラウザでPDFファイルを開く
   * @param base64Pdf base64のstring
   * @param fileCnt   総ファイル数
   * @param filename  ファイル名
   */
 openPdfInChromeBrowser(base64Pdf: string, fileCnt: number,filename?: string) {
    // base64 ヘッダーからメディア タイプを抽出する
    const base64Array = base64Pdf.split(';base64,', 2);
    const mimeType = base64Array[0].split(':', 2);

    // base64 ヘッダーがない場合は、base64 stringのシグネチャーからメディア タイプを判定する
    const mediaType = mimeType[1] || 'text/plain';
    // base64のstringからBLOBオブジェクトを作成
    const blob = this.fileUtil.convertToBlob(base64Pdf,this.fileUtil.mediaTypePdf);
    // eslint-disable-next-line
    const b: any = blob;

    let fileName = filename || 'ドキュメント';
    const index = fileName?.indexOf('.');
    // ファイル拡張子がないかをチェックする
    if(index === -1) {
      // メディア タイプからファイル拡張子を抽出する。「pdf」がデフォルトで設定されている。
      const extension = mediaType.split('/', 2)[1] || 'txt';
        // メディア タイプが text/plain の場合、拡張子を「txt」に設定する
      fileName = fileName + '.' + (extension === 'plain' ? 'txt' : extension);
    }
    b.name = fileName;

    const fileURL = URL.createObjectURL(b);
    const openedWindow = window.open(fileURL,this.TARGET_BLANK,this.getPdfWindowParameters());

    // 画面が起動できないときはポップアップブロックの設定があるので、エラーを表示する
    if(!openedWindow){
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId:'CSS01999M013E',});
      throw messageStack;
    }

    /* 複数ファイルを開くと画面の読み込みが完了する前にタイトル名が書き換わるので
       タイトル名の編集処理をファイル数*500ミリ秒遅らせる*/
    openedWindow.addEventListener('load',() =>{
      setTimeout(() => {
        openedWindow.document.title = fileName;
      },2000 * fileCnt);

    });
  }

  /**
 　* base64 PDF 文字列が PDF かどうかを判断する
  　* @param base64Pdf base64のstring
  　*/
  private isMediaTypePdf(base64PDF: string): boolean {
    const base64Data = base64PDF.replace(/^data:.*;base64,/, '');
    let isTypePdf = false;
    if (typeof base64Data === 'string') {
      isTypePdf = base64Data.startsWith('JVBER');
    }
    return isTypePdf;
  }

  /**
   * [一般開発者利用可]
   * 別Windowでbase64のstring型のPDFを開く
   * @param base64Pdf base64のstring型のPDF
   * @param filename ファイル名
   */
  openMediaTypePdfInChildWindow(base64Pdf: string, filename?: string): void {
    const viewPdfWindowInVO = new ViewPdfWindowInVO();
    viewPdfWindowInVO.filename = filename || 'ドキュメント.pdf';
    viewPdfWindowInVO.pdfBase64 = base64Pdf.replace(/^data:.*;base64,/, '');

    // 次画面に遷移するパラメータをセットする。ファイル名の競合を避けるために時刻情報を追加する。
    const tempId = viewPdfWindowInVO.filename + '-' + new Date().getTime();

    // QA#48660の対応：encodeRFC3986URIComponent() を使用して特殊文字をハンドルする
    const pdfScreenId = this.encodeRFC3986URIComponent(tempId);

    // base64 stringのPDFファイルをLocal Storageに保存する。pdfScreenId をキーとして使用する。
    this.setItemToLocalStorage<ViewPdfWindowInVO>(pdfScreenId, viewPdfWindowInVO);
    this.logger.debug('Local storage キー:' + pdfScreenId);

    const openedWindow = window.open('pages/view-pdf-window/' + pdfScreenId, this.TARGET_BLANK, this.getPdfWindowParameters());

    // 画面が起動できないときはポップアップブロックの設定があるので、エラーを表示する
    if(!openedWindow){
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M013E',});
      throw messageStack;
    }

    // PDFリーダー画面クローズ時のイベントを定義しておく
    (<Window>openedWindow).addEventListener(
      'unload',
      () => {
        // 子画面が開かれると、ブラウザは1回目アンロードイベントを実行する。
        // ユーザーがブラウザの閉じるボタンを押下すると、ブラウザは2回目アンロードイベントを実行する。
        // 1回目アンロードイベントを実行するときにコールバックと項目削除を回避するために、アンロードイベントに処理を再割り当てる必要がある。
        (<Window>openedWindow).addEventListener(
          'unload',
          () => {
            this.ngZone.run(() => {
              this.logger.debug('PDFリーダー画面をアンロードする:' + pdfScreenId);
              this.removeItemFromLocalStorage(pdfScreenId);
              this.removeItemFromSessionStorage(pdfScreenId);
              this.parentScreen = void 0;
          });
        });
      });
    (<Window>openedWindow).localStorage.setItem((<Window>openedWindow).name + 'isChildScreen', 'true');
  }

  /**
   * [一般開発者利用不可]
   * 特殊文字を置き換える。QA#48660 の対応。
   * 参照：https://github.com/angular/angular/issues/4895
   * @param stringUri URIの文字列
   * @private
  */
  encodeRFC3986URIComponent(stringUri: string): string {
    return encodeURIComponent(stringUri)
      .replace(
        /[!'()*]/g,
        (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`
      );
  }

  /**
   * [一般開発者利用可]
   * 別Windowでbase64のstring型のPDF以外を開く
   * @param base64Pdf base64のstring
   * @param filename ファイル名
   */
  openBase64OtherTypeInChildWindow(base64Pdf: string, filename?: string): void {
    // base64 ヘッダーからメディア タイプを抽出する
    const base64Array = base64Pdf.split(';base64,', 2);
    const mimeType = base64Array[0].split(':', 2);

    // base64 ヘッダーがない場合は、base64 stringのシグネチャーからメディア タイプを判定する
    const mediaType = mimeType[1] || 'text/plain';
    const mediaType2 = mediaType === 'application/pdf' ? 'text/plain' : mediaType;

    // base64のstringからBLOBオブジェクトを作成
    const blob = this.fileUtil.convertToBlob(base64Pdf, mediaType2);
    // eslint-disable-next-line
    const b: any = blob;
    b.lastModified = new Date();

    let fileName = filename || 'ドキュメント';
    const index = fileName?.indexOf('.');
    // ファイル拡張子がないかをチェックする
    if(index === -1) {
      // メディア タイプからファイル拡張子を抽出する。「pdf」がデフォルトで設定されている。
      const extension = mediaType.split('/', 2)[1] || 'txt';
        // メディア タイプが text/plain の場合、拡張子を「txt」に設定する
      fileName = fileName + '.' + (extension === 'plain' ? 'txt' : extension);
    }
    b.name = fileName;

    const fileURL = URL.createObjectURL(b);

    const openedWindow = window.open(fileURL, this.TARGET_BLANK, this.getPdfWindowParameters());
    // 画面が起動できないときはポップアップブロックの設定があるので、エラーを表示する
    if(!openedWindow){
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M013E',});
      throw messageStack;
    }
    (<Window>openedWindow).addEventListener(
      'load',
      () => {
        this.ngZone.run(() => {
            openedWindow.document.title = fileName;
        });
      }, false);

    // PDFリーダー画面クローズ時のイベントを定義しておく
    (<Window>openedWindow).addEventListener(
      'unload',
      () => {
        (<Window>openedWindow).addEventListener(
          'unload',
          () => {
            this.ngZone.run(() => {
              URL.revokeObjectURL(fileURL);
          });
        });
      });
    (<Window>openedWindow).localStorage.setItem((<Window>openedWindow).name + 'isChildScreen', 'true');
  }

  /**
   * [一般開発者利用可]
   * Printer windowsを直接に呼び出すことでbase64の文字列をPDF印刷する。
   * @param base64Pdf base64のstring型のPDF
   * @param dialogCloseCallback onPrintDialogCloseのコールバック
   */
  printBase64Pdf(base64Pdf: string, dialogCloseCallback?: () => void): void {
    const blob = this.fileUtil.convertToBlob(base64Pdf, this.fileUtil.mediaTypePdf);
    const blobUrl = URL.createObjectURL(blob);
    printJS({
      printable: blobUrl,
      type: 'pdf',
      onPrintDialogClose: dialogCloseCallback,
    });
  }

  /**
   * [一般開発者利用不可]
   * 画面定義情報からWindowをOpenする際の定義文字列を生成する
   * @param screenVO
   * @private
   */
  private getWindowParameters(screenVO: ScreenVO): string {
    const windowParamString: string[] = [];
    const screenWidth = screenVO.width ?? this.constant.DEFAULT_PC_SCREEN_WIDTH;
    const screenHeight = screenVO.height ?? this.constant.DEFAULT_PC_SCREEN_HEIGHT;
    windowParamString.push('width=' + screenWidth);
    windowParamString.push('height=' + screenHeight);
    windowParamString.push('toolbar=' + (screenVO.showToolBar ? 'yes' : 'no'));
    windowParamString.push('location=' + (screenVO.showLocation ? 'yes' : 'no'));
    windowParamString.push('status=' + (screenVO.showStatus ? 'yes' : 'no'));

    let topLocation = (screen.availHeight - screenHeight) / 2;
    let leftLocation = (screen.availWidth - screenWidth) / 2;

    if (screenVO.top != null) {
      topLocation = (screenVO.locationBasedOnParent ? window.screenTop : 0) + screenVO.top;
    }
    if (screenVO.left != null) {
      leftLocation = (screenVO.locationBasedOnParent ? window.screenLeft : 0) + screenVO.left;
    }
    if (screenVO.bottom != null) {
      topLocation = (screenVO.locationBasedOnParent ? (window.screenTop + window.innerHeight) : screen.height) - screenHeight - screenVO.bottom;
    }
    if (screenVO.right != null) {
      leftLocation = (screenVO.locationBasedOnParent ? (window.screenLeft + window.innerWidth) : screen.width) - screenWidth - screenVO.right;
    }

    windowParamString.push('left=' + leftLocation);
    windowParamString.push('top=' + topLocation);

    return windowParamString.join(',');
  }

  /**
   * [一般開発者利用不可]
   * PDFファイル表示用の子ウィンドウを開くときに定義文字列を生成する
   * @private
   */
  private getPdfWindowParameters(): string {
    const windowParamString: string[] = [];
    const screenWidth = this.constant.DEFAULT_PC_CHILD_SCREEN_WIDTH;
    const screenHeight = this.constant.DEFAULT_PC_CHILD_SCREEN_HEIGHT;
    windowParamString.push('width=' + screenWidth);
    windowParamString.push('height=' + screenHeight);
    windowParamString.push('toolbar=no');
    windowParamString.push('location=no');
    windowParamString.push('status=no');
    windowParamString.push('left=' + (screen.availWidth - screenWidth) / 2 );
    windowParamString.push('top=' + (screen.availHeight - screenHeight) / 2);
    return windowParamString.join(',');
  }

  /**
   * [一般開発者利用可]
   * 画面遷移パラメータの取得。
   * 事前に定義されたInVOの形式(<T>型として指定)で画面遷移元からセットされたパラメータを取得する
   */
  getSharingParams<T>(): T | null {
    if(this.screenInfo?.screenId){
      return this.getItemFromLocalStorage<T>(this.screenInfo?.screenId);
    } else {
      return null;
    }
  }

  /**
   * [一般開発者利用可]
   * 当該子画面の画面IDをキーにOutVOの形式(<T>型として指定)で返却パラメータをLocal Storageに保存し、
   * 子画面のウィンドウをクローズする。
   * @param param 子画面Close時にCallBackに渡すパラメータ。なければ引数なしでコール。
   */
  closeChild<T>(params?: T): void {
    // 親画面にパラメータを返却する
    if(this.screenInfo?.screenId && params){
      this.setItemToLocalStorage<T>(this.screenInfo.screenId, params);
    }
    window.close();
  }

  /**
   * [一般開発者利用可]
   * 子画面側でエラーが発生した際に、親画面にエラーを引き渡す。エラー情報をLocal Storageに保存し、
   * 子画面のウィンドウをクローズする。
   */
  closeChildWithError(params?: ErrorListOnTransitionVO): void {
    // 親画面にパラメータを返却する
    if(this.screenInfo?.screenId && params){
      this.setItemToLocalStorage<ErrorListOnTransitionVO>(this.screenInfo.screenId, params);
    }
    window.close();
  }

  /**
   * [一般開発者利用可]
   * 特定のWindowをフォーカスする
   * @param screenId
   */
  setFocus(screenId: string): void {
    const screenWindow = window.open('', screenId);
    screenWindow?.focus();
  }

  /**
   * [一般開発者利用可]
   * 元のWindowからオープンしたwindowを全て閉じる.
   */
  closeAll(): void {
    for(const window of this.windowStack.get().values()) {
      window.close();
    }
  }

  /**
   * [一般開発者利用可]
   * 指定の画面IDが存在するか確認する.
   * @param screenId 画面機能ID
   */
  hasScreenId(screenId: string): boolean {
    return this.isAlive(screenId);
  }

  /**
   * [一般開発者利用可]
   * 画面の呼び出し元が存在するか。
   * @return 子画面の場合true, 親画面（メイン画面）の場合falseが返る。
   */
  hasCaller(): boolean {
    if(window.opener) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * [一般開発者利用不可]
   * 当該ScreenIDが開いた状態かを確認
   * @param screenId 画面機能ID
   * @private
   */
  private isAlive(screenId: string): boolean {
    const screenDefinition = this.screenDefinitionProvider.getScreenDefinition(screenId);
    if (!screenDefinition || !screenDefinition.path) {
      return false;
    } else {
      const scrwindow = window.open('', screenId, this.getWindowParameters(screenDefinition));
      if (scrwindow == null) {
        return false;
      }

      let isBlankPage = true;
      if (scrwindow.location.href === this.BLANK) {
        isBlankPage = false;
        if (!scrwindow.closed) {
          scrwindow.close();
        }
      }
      return isBlankPage;
    }
  }

  /**
   * [一般開発者利用可]
   * 子画面から親画面のメソッドを呼び出す。
   * @param methodName 親画面の呼び出すメソッドの名前
   * @param parameter 親画面の呼び出すメソッドのパラメータ
   *                  （親画面のメソッドはパラメータがない場合、このパラメータを渡さない）
   */
  callMethodFromParent<T>(methodName: string, parameter?: T): void {
    // 親画面のメソッドを呼び出す。
    window.parent.postMessage({
      type: 'callMethodFromParent',
      methodName: methodName,
      hasParameter: parameter != null,
      parameter: JSON.stringify(parameter),
      childScreenId: window.name.substring(0, 8),
    }, location.origin);

    // 以下の場合にエラーログに書き込み、メッセージを表示する。
    // 1.親画面にメソッドが存在しない。
    // 2.親画面のメソッドの処理で例外が発生した。
    const handleError = (event: MessageEvent) => {
      this.ngZone.run(() => {
        if (event.data.type === 'callMethodFromParentError') {
          this.logger.error('CSS01999M038E');
          const messageStack = new MessageStackService(MessageLevel.ERROR);
          messageStack.add({messageId: 'CSS01999M001E',});
          this.thrownErrorHandler.handleError(messageStack);

          window.removeEventListener('message', handleError);
        }
      });
    };
    window.addEventListener('message', handleError);
  }

  /**
   * [一般開発者利用可]
   * 子画面から受信したObjectからOutVOへデータを変換する
   * @param vo 子画面のOutVO
   * @param obj 子画面から受信されたObject
   */
  // eslint-disable-next-line
  receiveDataFromChild<T>(vo: T, obj: any): T {
    // VOではDecimalライブラリを使用しているため、window.postMessage()でVOを親画面から子画面に直接送信できない。
    // この制限を対応するには、
    // - 子画面にて、
    //     + JSON.stringifyメソッドでVOからJSONに変換する
    //     + データがJSONのpostMessage
    // - 親画面にて、
    //     + 「message/callMethodFromParent」を受信できた時、JSONからObjectに変換し、親画面で呼び出すメソッドにObjectを渡す
    //     + 親画面はOutVOを宣言し、このメソッドを使用してObjectからOutVOに変換する。
    // eslint-disable-next-line
    const transferData = (obj1: T, obj2: any): T => {
      for (const p in obj2) {
        try {
          if (obj1[p] instanceof Logger || obj1[p] instanceof ChangeTypeUtil) {
            // Exclude: Logger & ChangeTypeUtil
          } else if (obj1[p] instanceof Decimal) {
            obj1[p] = new Decimal(obj2[p]);
          } else if (obj1[p] instanceof Date) {
            obj1[p] = new Date(obj2[p]);
          } else if (typeof obj2[p] === 'object') {
            obj1[p] = transferData(obj1[p], obj2[p]);
          } else {
            Object.assign(obj1[p], obj2[p]);
          }
        } catch (e) {
          obj1[p] = obj2[p];
        }
      }
      return obj1;
    }
    transferData(vo, obj);
    return vo;
  }

  /**
   * [一般開発者利用可]
   * LocalStorageに指定されたKeyとパラメータを保存する
   * @param key 保存するキー
   * @param item 保存するパラメータ。JSON形式かStringかnullをサポート。
   * nullの場合はアイテムを削除。
   */
  setItemToLocalStorage<T>(key: string, item: T | string | null): void {
    if (item != null) {
      this.logger.debug('以下の情報をローカルストレージに保存します。:', item);
      if(typeof item === 'object'){
        window.localStorage.setItem(key, JSON.stringify(item));
      } else if(typeof item === 'string'){
        window.localStorage.setItem(key, item);
      }
    } else {
      this.logger.debug('ローカルストレージに保存しようとするパラメータがnullかundefinedなので、キーに対するStorageを削除:', key);
      window.localStorage.removeItem(key);
    }
  }

  /**
   * [一般開発者利用可]
   * LocalStorageから指定されたKeyでパラメータを抽出する。
   * @param key 取得するキー(画面遷移パラメータの場合は画面ID)
   */
  getItemFromLocalStorage<T>(key: string): T | null {
    const rawData = window.localStorage.getItem(key);
    if(rawData) {
      return this.jsonUtil.parse<T>(rawData);
    } else {
      return null;
    }
  }

  /**
   * [一般開発者利用可]
   * SessionStorageに指定されたKeyとパラメータを保存する
   * @param key 保存するキー
   * @param item 保存するパラメータ。JSON形式かStringかnullをサポート。
   * nullの場合はアイテムを削除。
   */
  setItemToSessionStorage<T>(key: string, item: T | string | null): void {
    if (item != null) {
      this.logger.debug('以下の情報をセッションストレージに保存します。:', item);
      if(typeof item === 'object'){
        window.sessionStorage.setItem(key, JSON.stringify(item));
      } else if(typeof item === 'string'){
        window.sessionStorage.setItem(key, item);
      }
    } else {
      this.logger.debug('セッションストレージに保存しようとするパラメータがnullかundefinedなので、キーに対するStorageを削除:', key);
      window.sessionStorage.removeItem(key);
    }
  }

  /**
   * [一般開発者利用可]
   * SessionStorageから指定されたKeyでパラメータを抽出する。
   * @param key 取得するキー(画面遷移パラメータの場合は画面ID)
   * @private
   */
  getItemFromSessionStorage<T>(key: string): T | null {
    const rawData = window.sessionStorage.getItem(key);
    if(rawData) {
      return this.jsonUtil.parse<T>(rawData);
    } else {
      return null;
    }
  }

  /**
   * [一般開発者利用可]
   * 指定したキーに対するデータをLocalStorageから削除する
   * @param key 削除したいデータのキー
   */
  removeItemFromLocalStorage(key: string): void {
    window.localStorage.removeItem(key);
  }

  /**
   * [一般開発者利用可]
   * 指定したキーに対するデータをSessionStorageから削除する
   * @param key 削除したいデータのキー
   * @private
   */
  removeItemFromSessionStorage(key: string): void {
    window.sessionStorage.removeItem(key);
  }

  /**
   * [一般開発者利用可]
   * 画面遷移前などFormに変更が加えられていないかを確認することを目的として
   * 事前にチェック対象のFormを登録しておく。ここに登録しておくと
   * checkFormIsDirtyメソッドで遷移前に、そのFormに対して何らかの変更が入ったかをチェックできる
   * @param forms FormGroupもしくはFormControl型のFormオブジェクト(可変数引数なので必要なだけ登録できる)
   */
  registerFormForDirtyCheck(...forms: (FormGroup | FormControl)[]): void {
    this.forms = forms;
  }

  /**
   * [一般開発者利用可]
   * 画面遷移前などFormに変更が加えられていないかを確認する
   */
  checkFormIsDirty(): boolean {
    for(const form of this.forms) {
      if(form.dirty) {
        return true;
      }
    }
    return false;
  }

  /**
   * [一般開発者利用可]
   * 登録・更新ボタン押下時などでクライアントサイドのFormにバリデーションエラーがあるかチェックし、
   * エラーが残っている場合は、フォームをDirty(手を加えた)状態にしてエラーを表示する。処理も中断する
   * @param formGroup フォームグループ
   * @param messageStack バリデーションエラーがある場合に表示する任意のメッセージ
   * (任意指定パラメータ。設定なしの場合、SMS99901M011Eを表示)
   */
  checkFormValidityAndShowError(formGroup: FormGroup,
                                messageStack: MessageStackService = new MessageStackService(MessageLevel.ERROR)
  ): void {
    // クライアントサイド入力バリデーション抑制モードの時(開発時のみ、サーバーサイドの入力バリデーションテスト用)
    if ('ignore_client_validation_for_testing' in environment && environment.ignore_client_validation_for_testing) {
      // サーバーサイドのバリデーションエラーを一旦クリアする
      formGroup.setErrors({'serverSideInputValidation': false,});
      // フォームをDirty(手を加えた)状態からUntouched(手を加えていない状態)にして、処理を続行する(サーバーからのエラーを表示するため)
      this.markAllControlsAsUntouched(Object.values(formGroup.controls));
      // 通常モードの時
    } else {
      if (!formGroup.valid) {
        // フォームをDirty(手を加えた)状態にしてエラーを表示する
        this.markAllControlsAsDirty(Object.values(formGroup.controls));
        if (messageStack.length() === 0) {
          messageStack.add({messageId: 'SMS99901M011E',});
        }
        this.moveToFirstErrorItemAndShowError(formGroup);
        throw messageStack;
      }
    }
  }

  /**
   * [一般開発者利用可]
   * checkFormValidityAndShowErrorの複数フォーム対応版
   * @param formGroups フォームグループの配列
   * @param messageStack バリデーションエラーがある場合に表示する任意のメッセージ
   * (任意指定パラメータ。設定なしの場合、SMS99901M011Eを表示)
   */
  checkMultipleFormValidityAndShowError(formGroups: FormGroup[],
    messageStack: MessageStackService = new MessageStackService(MessageLevel.ERROR)
  ): void {
    // クライアントサイド入力バリデーション抑制モードの時(開発時のみ、サーバーサイドの入力バリデーションテスト用)
    if ('ignore_client_validation_for_testing' in environment && environment.ignore_client_validation_for_testing) {
      formGroups.forEach((formGroup: FormGroup) => {
        // サーバーサイドのバリデーションエラーを一旦クリアする
        formGroup.setErrors({'serverSideInputValidation': false,});
        // フォームをDirty(手を加えた)状態からUntouched(手を加えていない状態)にして、処理を続行する(サーバーからのエラーを表示するため)
        this.markAllControlsAsUntouched(Object.values(formGroup.controls));
      });
    // 通常モードの時
    } else {
      const formGroupsHavingError: FormGroup[] = new Array<FormGroup>();
      formGroups.forEach((formGroup: FormGroup) => {
        if (!formGroup.valid) {
          // フォームをDirty(手を加えた)状態にしてエラーを表示する
          this.markAllControlsAsDirty(Object.values(formGroup.controls));
          formGroupsHavingError.push(formGroup);
        }
      });
      if(formGroupsHavingError.length > 0) {
        if (messageStack.length() === 0) {
          messageStack.add({messageId: 'SMS99901M011E',});
        }
        this.moveToFirstErrorItemAndShowError(...formGroupsHavingError);
        throw messageStack;
      }
    }
  }

  /**
   * [一般開発者利用不可]
   * 登録・更新ボタン押下時などでクライアントサイドのFormがValidでない場合に、
   * フォームをDirty(手を加えた)状態にしてエラーを表示する
   * @param abstractControls FormGroup内のControlのリスト。Object.values(this.form.controls)で取得できる
   * @private
   */
  private markAllControlsAsDirty(abstractControls: AbstractControl[]): void {
    abstractControls.forEach(abstractControl => {
      if (abstractControl instanceof FormControl) {
        (abstractControl as FormControl).markAsDirty({onlySelf: true,});
      } else if (abstractControl instanceof FormGroup) {
        this.markAllControlsAsDirty(Object.values((abstractControl as FormGroup).controls));
      } else if (abstractControl instanceof FormArray) {
        this.markAllControlsAsDirty((abstractControl as FormArray).controls);
      }
    });
  }

  /**
   * [一般開発者利用不可]
   * 与えられたフォームをUntouched(手を加えていない)状態にしてエラーを表示する
   * @param abstractControls FormGroup内のControlのリスト。Object.values(this.form.controls)で取得できる
   * @private
   */
  private markAllControlsAsUntouched(abstractControls: AbstractControl[]): void {
    abstractControls.forEach(abstractControl => {
      if (abstractControl instanceof FormControl) {
        (abstractControl as FormControl).markAsUntouched({onlySelf: true,});
      } else if (abstractControl instanceof FormGroup) {
        this.markAllControlsAsUntouched(Object.values((abstractControl as FormGroup).controls));
      } else if (abstractControl instanceof FormArray) {
        this.markAllControlsAsUntouched((abstractControl as FormArray).controls);
      }
    });
  }

  /**
   * [一般開発者利用不可]
   * 画面の最初のエラーになった項目へ移動しフォーカスを充ててエラーメッセージを表示
   * @param formGroup フォームグループ (複数可能)
   * @private
   */
  private moveToFirstErrorItemAndShowError(...formGroups: FormGroup[]): void {
    // FormGroupをフラット配列に変換し、エラーのある項目を抽出
    const errorItems: AbstractControl[] = formGroups.reduce((result, formGroup) => {
      result.push(...Object.values<AbstractControl>(formGroup.controls));
      return result;
    }, <AbstractControl[]>[])
    .filter(control => control.invalid).reduce((result, control) => {
      if (control instanceof FormControl) {
        result.push(control);
      } else if (control instanceof FormGroup) {
        result.push(...(Object.values((control as FormGroup).controls)
          .filter(control => control.invalid)));
      } else if (control instanceof FormArray) {
        result.push(...(control as FormArray).controls);
      }

      return result;
    }, <AbstractControl[]>[]);

    // エラーがある項目はない場合、メソッドから退出
    if (errorItems.length <= 0) {
      return;
    }

    // タブの中にあって一回も表示したことがない項目を抽出
    const errorItemsHidden = errorItems.filter(item => {
      if (!item['bounds']) {
        item['bounds'] = (<HTMLElement>item['nativeElement']).getBoundingClientRect();
      }
      return Math.max(item['bounds'].top, item['bounds'].left) === 0;
    });

    // エラーになった項目の全ては項目配置が取得できた場合、最初のエラー項目へ移動
    if (errorItemsHidden.length <= 0) {
      const itemToFocus = this.getFirstItemOnScreen(errorItems);
      this.moveToItemAndSetFocus(itemToFocus);
      return;
    }

    // 項目配置が取得できていないエラー項目がある場合, ページを超えるの処理を実施 (Tabあり画面のはず)
    const stepper = document.querySelector('mat-stepper');
    // 画面はStepper-Tab形の場合
    if (stepper) {

      // 超える対象のページ(ステップ・タブ)を判断し、クリックを行う順番で配列に格納
      const pagesToClick = Array.from(stepper.querySelectorAll('mat-tab-group'))
        .reduce((result, tabGroup) => {

          const tabsInStep = Array.from(tabGroup.querySelectorAll('[id*="tab-content"]'))
            .map(tab => tab.id);
          if (tabsInStep.length <= 0) {
            return result;
          }

          const step = tabGroup.parentElement?.closest('[id*="step-content"]')?.id;
          if (!step) {
            return result;
          }

          // タブを持ってるステップとそのステップの中にあるタブを格納 (最初のタブを除く)
          result.push(step, ...tabsInStep.slice(1));
          return result;
      }, <string[]>[]);

      // タブのあるステップの前のページにエラー項目がある場合、全ページを超えるのが不要
      const stepsWithTabs = pagesToClick.filter(page => page.includes('step'));
      if (stepsWithTabs.length > 0 &&
        errorItems.some(item => item['stepElementId'] < stepsWithTabs[0])) {
          const itemToFocus = this.getFirstItemOnScreen(errorItems);
          this.moveToItemAndSetFocus(itemToFocus);
          return;
      }

      // 配列の順番で超える対象のページを連続クリックし表示
      let i = 0;
      const traverseAllTabsInterval = setInterval(() => {

        // 可能な最初のエラーになった項目が見つかったまたは、超える対象のページを全部クックした場合、最初のエラー項目へ移動
        const isFirstItemFound = (i > 0) && errorItems.some(item => {
          return pagesToClick[i-1] === item['tabFirstElementId'] ||
          (pagesToClick.slice(0, i).includes(item['stepElementId']) &&
          item['tabFirstElementId']?.endsWith('-0'));
        });
        if (isFirstItemFound || i >= pagesToClick.length) {
          const itemToFocus = this.getFirstItemOnScreen(errorItems);
          this.moveToItemAndSetFocus(itemToFocus);
          clearInterval(traverseAllTabsInterval);
          return;
        }

        // ページをクリック。クリック対象の画面項目が見つからない場合、連続クリック処理を中断
        const elementToClick =
          <HTMLElement>document.querySelector(`[aria-controls="${pagesToClick[i]}"]`);
        if (!elementToClick) {
          clearInterval(traverseAllTabsInterval);
        }
        elementToClick.click();

        i++;
      }, 200);

      return;
    }

    // 画面はTab-Tab形かTabのみ形の場合
    // 第1タブを取得 (第2タブを除く)
    const tabsToClick = Array.from(document.querySelectorAll('mat-tab-body'))
      .filter(tab => !tab.parentElement?.closest('mat-tab-body')).map(tab => tab.id);

    if (tabsToClick.length > 0) {

      // 最初の第1タブにはエラーになった項目がある場合、タブを超えるのが不要
      if (errorItems.some(item => item['tabFirstElementId'] === tabsToClick[0])) {
          const itemToFocus = this.getFirstItemOnScreen(
            errorItems.filter(item => !errorItemsHidden.includes(item)));
          this.moveToItemAndSetFocus(itemToFocus);
          return;
      }

      // 配列の順番で超える対象のタブを連続クリックし表示
      let i = 0;
      const mainTabIdPattern = tabsToClick[0].slice(0, tabsToClick[0].lastIndexOf('-'));
      const traverseAllTabsInterval = setInterval(() => {

        // 第1タブの中に第2タブがある場合、クリック対象のタブに追加
        let childTabsInMainTab;
        if (i > 0) {
          childTabsInMainTab =
            Array.from(document.querySelectorAll(`#${tabsToClick[i-1]} mat-tab-body`)).map(tab => tab.id);
          if (childTabsInMainTab.length > 1) {
            tabsToClick.splice(i, 0, ...childTabsInMainTab.slice(1));
          }
        }

        // 可能な最初のエラーになった項目が見つかったまたは、超える対象のタブを全部クックした場合、最初の項目へ移動
        const isFirstItemFound = (i > 0) && errorItems.some(item => {
          if (childTabsInMainTab?.length > 0) {
            return item['tabFirstElementId'] === tabsToClick[i-1]
              && item['tabSecondElementId'].endsWith('-0');
          }
          if (!tabsToClick[i-1].includes(mainTabIdPattern)) {
            return item['tabSecondElementId'] === tabsToClick[i-1];
          }
          return item['tabFirstElementId'] === tabsToClick[i-1];
        });
        if (isFirstItemFound || i >= tabsToClick.length) {
          const itemToFocus = this.getFirstItemOnScreen(errorItems);
          this.moveToItemAndSetFocus(itemToFocus);
          clearInterval(traverseAllTabsInterval);
          return;
        }

        // タブをクリック。クリック対象の画面項目が見つからない場合、連続クリック処理を中断
        const elementToClick =
          <HTMLElement>document.querySelector(`div[aria-controls="${tabsToClick[i]}"]`);
        if (!elementToClick) {
          clearInterval(traverseAllTabsInterval);
        }
        elementToClick.click();

        i++;
      }, 200);

      return;
    }

    // 画面はAngular標準を使用せず実装されるTab形の場合
    // 画面例：ESE01006, ESE01007
    // 1番目のタブにエラー項目のある場合、タブを超えるのが不要
    if (errorItems.some(item => item['contentDivIndex'] === 0)) {
      const itemToFocus = this.getFirstItemOnScreen(
        errorItems.filter(item => {
          return Math.max(item['bounds'].top, item['bounds'].left) !== 0;
        })
      );
      this.moveToItemAndSetFocus(itemToFocus);
      return;
    }

    // エラー項目の位置を取得できるように該当タブの表示を実行する必要がある
    const parentTabBox = errorItemsHidden[0]['nativeElement'].parentElement?.closest('.tab-box');
    const tabLabels = Array.from(parentTabBox?.querySelectorAll('.tab_class')) as HTMLElement[];
    const tabIndex = Math.min(...errorItemsHidden.map(item => item['contentDivIndex']));
    if (tabLabels[tabIndex]) {
      // 該当タブに移動
      tabLabels[tabIndex].click();

      // エラー項目の位置を取得し格納
      errorItemsHidden.forEach(item => {
        item['bounds'] = item['nativeElement'].getBoundingClientRect();
      });

      const itemToFocus = this.getFirstItemOnScreen(
        errorItems.filter(item => {
          return Math.max(item['bounds'].top, item['bounds'].left) !== 0;
        })
      );
      this.moveToItemAndSetFocus(itemToFocus);
    }

  }

  /**
   * [一般開発者利用不可]
   * 画面項目の中に画面上の最初の項目を判断し返却
   * @param screenItems 画面項目
   * @return 画面上の最初の項目
   * @private
   */
  private getFirstItemOnScreen(screenItems: AbstractControl[]): AbstractControl {
    // 各項目のページインデクスを判断し、それぞれに該当する変数に格納する
    screenItems = screenItems.filter(item => Math.max(item['bounds'].top, item['bounds'].left) !== 0);
    screenItems.forEach(item => {
      if (item['stepElementId']) {
        const stepIdSplit = item['stepElementId'].split('-');
        item['stepIndex'] = parseInt(stepIdSplit[stepIdSplit.length - 1]);
      }

      if (item['tabFirstElementId']) {
        const tabFirstIdSplit = item['tabFirstElementId'].split('-');
        item['tabFirstIndex'] = parseInt(tabFirstIdSplit[tabFirstIdSplit.length - 1]);
      }

      if (item['tabSecondElementId']) {
        const tabSecondIdSplit = item['tabSecondElementId'].split('-');
        item['tabSecondIndex'] = parseInt(tabSecondIdSplit[tabSecondIdSplit.length - 1]);
      }

      if (item['accordionElementId']) {
        const accordionIdSplit = item['accordionElementId'].split('-');
        item['accordionIndex'] = parseInt(accordionIdSplit[accordionIdSplit.length - 1]);
      }
    });

    // 最初の項目を判定
    const firstScreenItem = screenItems.reduce((result, control) => {

      if (result['stepIndex'] !== null && control['stepIndex'] !== null) {
        result = result['stepIndex'] - control['stepIndex'] > 0 ? control : result;
        if (result['stepIndex'] !== control['stepIndex']) {
          return result;
        }
      }

      if (result['tabFirstIndex'] !== null && control['tabFirstIndex'] !== null) {
        result = result['tabFirstIndex'] - control['tabFirstIndex'] > 0 ? control : result;
        if (result['tabFirstIndex'] !== control['tabFirstIndex']) {
          return result;
        }
      }

      if (result['tabSecondIndex'] !== null && control['tabSecondIndex'] !== null) {
        result = result['tabSecondIndex'] - control['tabSecondIndex'] > 0 ? control : result;
        if (result['tabSecondIndex'] !== control['tabSecondIndex']) {
          return result;
        }
      }

      if (result['contentDivIndex'] !== null && control['contentDivIndex'] !== null) {
        result = result['contentDivIndex'] - control['contentDivIndex'] > 0 ? control : result;
        if (result['contentDivIndex'] !== control['contentDivIndex']) {
          return result;
        }
      }

      if (result['accordionIndex'] !== null && control['accordionIndex'] !== null) {
        result = result['accordionIndex'] - control['accordionIndex'] > 0 ? control : result;
        if (result['accordionIndex'] !== control['accordionIndex']) {
          return result;
        }
      }

      if (result['bounds'].top - control['bounds'].top > 0) {
        result = control;
      }
      if (result['bounds'].top !== control['bounds'].top) {
        return result;
      }

      if (result['bounds'].left - control['bounds'].left > 0) {
        result = control;
      }

      return result;
    }, screenItems[0]);

    return firstScreenItem;
  }

  /**
   * [一般開発者利用可]
   * 指定する画面項目に遷移し、フォーカスを充てる
   * @param elementToFocus フォーカス対象の画面項目
   * @param checkbox (任意指定パラメータ) 対象項目のチェックボックス
   */
  async moveToItemAndSetFocus(elementToFocus: HTMLElement | AbstractControl,
    checkbox?: MatCheckbox | null): Promise<void> {

    return new Promise<void>(resolve => {

      const CLICK_TIME_INTERVAL = 150;
      const isAbstractControl = elementToFocus instanceof AbstractControl;
      const itemToFocus = isAbstractControl ?
        elementToFocus['nativeElement'] : elementToFocus;
      let bounds = isAbstractControl && elementToFocus['bounds'] ?
        elementToFocus['bounds'] : itemToFocus.getBoundingClientRect();

      // 画面がStepperのみかStepperもTabもない場合に対する対応
      if (Math.max(bounds.top, bounds.left) !== 0) {
        const itemsToClick: string[] = [];
        const step = isAbstractControl ? elementToFocus['stepElementId']
          : itemToFocus.parentElement?.closest('[id*="step-content"]')?.id;
        const tab = isAbstractControl ? elementToFocus['tabSecondElementId']
          : itemToFocus.parentElement?.closest('[id*="tab-content"]')?.id;
        const parentTab = isAbstractControl ? elementToFocus['tabFirstElementId']
          : tab?.parentElement?.closest('[id*="tab-content"]')?.id;
        const accordion = isAbstractControl ? elementToFocus['accordionElementId']
          : itemToFocus.parentElement?.closest('[id*="accordion-child"]')?.id;
        const contentDiv = isAbstractControl ? elementToFocus['contentDivIndex']
          : Array.from(document.querySelectorAll('.tab-box .content_class')).indexOf(
            itemToFocus.parentElement?.closest('.tab-box .content_class'));

        // focusが当たる項目まで遷移するためにクリックするページをクリック順序で配列に格納
        if (step) {
          itemsToClick.push(step);
        }
        if (parentTab) {
          itemsToClick.push(parentTab);
        }
        if (tab) {
          itemsToClick.push(tab);
        }
        if (contentDiv != null && contentDiv >= 0) {
          itemsToClick.push(contentDiv);
        }
        if (accordion) {
          itemsToClick.push(accordion);
        }

        // focusが当たる項目まで遷移し、項目にフォーカスを充てる
        let i = 0;
        const clickActionsInterval = setInterval(() => {

          // クリック対象のページが全部クリックした場合、focus項目にフォーカスを充てる
          if (i >= itemsToClick.length) {
            this.setFocusOnElement(itemToFocus, checkbox);
            resolve();
            clearInterval(clickActionsInterval);
            return;
          }

          // クリック対象の画面項目を取得。見つからない場合、処理を中止
          const elementToClick = isNaN(parseInt(itemsToClick[i])) ?
            document.querySelector(`[aria-controls="${itemsToClick[i]}"]`) as HTMLElement :
            Array.from(document.querySelectorAll('.tab-box .tab_class'))[parseInt(itemsToClick[i])] as HTMLElement;
          if (!elementToClick) {
            resolve();
            clearInterval(clickActionsInterval);
            return;
          }

          // クリック対象の画面項目をクリック。選択中の場合、スキップ
          if ((itemsToClick[i] !== accordion && elementToClick.getAttribute('aria-selected') === 'false')
            || elementToClick.getAttribute('aria-expanded') === 'false'
            || (itemsToClick[i] === contentDiv &&
            !(<HTMLInputElement>elementToClick.previousElementSibling?.closest('.tab-box input')).checked)) {
            elementToClick.click();
          }

          i++;
        }, CLICK_TIME_INTERVAL);

        return;
      }

      // focus項目が含めるアコーディオンを取得
      const accordion = itemToFocus.parentElement
        ?.closest('[id*="accordion-child"]') as HTMLElement;

      // 画面上にあるステッパーと第1タブグループを取得 (第2タブグループを除く)
      const steppers = Array.from(document.querySelectorAll('mat-stepper'))
        .filter(stepper => !stepper.parentElement?.closest('mat-stepper'));
      const tabGroups = Array.from(document.querySelectorAll('mat-tab-group'))
        .filter(tabGroup => !tabGroup.parentElement?.closest('mat-tab-group'));

      // 以前の画面のStepperも取得されるので、現在の画面にStepperがあるのか判断
      let hasStepper;
      if (steppers.length <= 0) {
        hasStepper = false;
      } else if (steppers.length > 1) {
        hasStepper = true;
      } else {
        hasStepper = tabGroups.length > 0 &&
          Array.from(steppers[0].querySelectorAll('mat-tab-group'))
          .includes(tabGroups[tabGroups.length-1]);
      }

      // 画面がStepper-Tabの場合に対する対応
      if (hasStepper) {

        // 閲覧対象のページ(ステップ・タブ)を判断し、クリックを行う順番で配列に格納
        const itemsToClick = Array.from(steppers[steppers.length-1]
          .querySelectorAll('mat-tab-group')).reduce((result, tabGroup) => {

            const tabsInStep = Array.from(tabGroup.querySelectorAll('[id*="tab-content"]'))
              .map(tab => tab.id);
            if (tabsInStep.length <= 0) {
              return result;
            }

            const step = tabGroup.parentElement?.closest('[id*="step-content"]')?.id;
            if (!step) {
              return result;
            }

            // タブを持ってるステップとそのステップの中にあるタブを格納 (最初のタブを除く)
            result.push(step, ...tabsInStep.slice(1));
            return result;
        }, <string[]>[]);

        // 配列の順序で閲覧対象のページを連続クリックし表示
        let i = 0;
        const clickActionsInterval = setInterval(() => {

          // focus項目が画面上に見えている場合
          bounds = itemToFocus.getBoundingClientRect();
          if (Math.max(bounds.top, bounds.left) !== 0 || i >= itemsToClick.length) {

            // focus項目があるアコーディオンの中身が非表示している場合, アコーディオンをクリックし開ける
            // その以外の場合、focus項目にフォーカスを充てる
            const isAccordionClickNeed = accordion &&
              window.getComputedStyle(accordion).visibility === 'hidden';
            if (!isAccordionClickNeed) {
              this.setFocusOnElement(itemToFocus, checkbox);
              resolve();
              clearInterval(clickActionsInterval);
              return;
            }

            itemsToClick.splice(i, 0, accordion.id);
          }

          // ページをクリックし表示。クリック対象のページが見つからない場合、処理を中止
          const elementToClick =
            <HTMLElement>document.querySelector(`[aria-controls="${itemsToClick[i]}"]`);
          if (!elementToClick) {
            resolve();
            clearInterval(clickActionsInterval);
            return;
          }
          if (elementToClick.getAttribute('aria-selected') === 'false'
            || itemsToClick[i] === accordion?.id) {
            elementToClick.click();
          }

          i++;
        }, CLICK_TIME_INTERVAL);

        return;
      }

      // 画面がTab-TabかTabのみの場合に対する対応
      const itemsToClick = Array.from(tabGroups[tabGroups.length-1].querySelectorAll('mat-tab-body'))
        .filter(tab => !tab.parentElement?.closest('mat-tab-body')).map(tab => tab.id);

      // 配列の順序で閲覧対象のページを連続クリックし表示
      let i = 0;
      const clickActionsInterval = setInterval(() => {

        // 第1タブの中に第2タブがある場合、閲覧対象タブの配列に追加
        if (i > 0) {
          const childTabs = Array.from(document
            .querySelectorAll(`#${itemsToClick[i-1]} mat-tab-body`)).map(tab => tab.id);
          if (childTabs.length > 1 && !itemsToClick.includes(childTabs[1])) {
            itemsToClick.splice(i, 0, ...childTabs.slice(1));
          }
        }

        // focus項目が画面上に見えている場合
        bounds = itemToFocus.getBoundingClientRect();
        if (Math.max(bounds.top, bounds.left) !== 0 || i >= itemsToClick.length) {

          // focus項目があるアコーディオンの中身が非表示している場合, アコーディオンをクリックし開ける
          // その以外の場合、focus項目にフォーカスを充てる
          const isAccordionClickNeed = accordion &&
            window.getComputedStyle(accordion).visibility === 'hidden';
          if (!isAccordionClickNeed) {
            this.setFocusOnElement(itemToFocus, checkbox);
            resolve();
            clearInterval(clickActionsInterval);
            return;
          }

          itemsToClick.splice(i, 0, accordion.id);
        }

        // ページをクリックし表示。クリック対象のページが見つからない場合、処理を中止
        const elementToClick =
            <HTMLElement>document.querySelector(`[aria-controls="${itemsToClick[i]}"]`);
        if (!elementToClick) {
          resolve();
          clearInterval(clickActionsInterval);
          return;
        }
        if (elementToClick.getAttribute('aria-selected') === 'false'
          || itemsToClick[i] === accordion?.id) {
          elementToClick.click();
        }

        i++;
      }, CLICK_TIME_INTERVAL);
    });
  }

  /**
   * [一般開発者利用不可]
   * エラーになった項目の中で最初の項目を判断し返却
   * @param elementToFocus フォーカス対象の画面項目
   * @param checkbox (任意指定パラメータ) 対象項目のチェックボックス
   * @private
   */
  private setFocusOnElement(elementToFocus: HTMLElement, checkbox?: MatCheckbox | null): void {
    if (elementToFocus.className.includes('mat-checkbox')) {
      if (checkbox) {
        checkbox.focus();
      }
    } else {
      elementToFocus.focus();
    }
  }

  /**
   * [一般開発者利用可]
   * Gridのデータのソートイベントをカスタムに実行
   * @param event Gridのソートイベントパラメータ
   */
  customSortGrid(event: SortEvent): void {

    const targetColumn = event.field;
    if (!targetColumn || !event.data) {
      return;
    }

    // 対象データのカスタムソートの実施
    event.data?.sort((data1, data2) => {
      const value1 = data1[targetColumn];
      const value2 = data2[targetColumn];
      let result = 0;

      // 比較対象データのいずれかがnull/undefinedの場合
      if (value1 == null || value2 == null) {
        result = value1 == null && value2 == null ? 0 : (value1 == null ? -1 : 1);
      } else if (typeof value1 === 'string' && typeof value2 === 'string') {

        // 比較対象データの両方がstring型である場合
        // 先頭が丸番号であるかの判定
        const circleNumPattern = new RegExp('^[\u2460-\u2473]');
        const value1Number = value1.match(circleNumPattern);
        const value2Number = value2.match(circleNumPattern);

        // 丸番号の場合、Unicodeで比較処理を実行
        if (value1Number?.length && value2Number?.length && value1Number[0] !== value2Number[0]) {
          result = value1Number[0].charCodeAt(0) - value2Number[0].charCodeAt(0);
        } else {
          // その以外の場合、普通の比較処理を実行
          result = value1.localeCompare(value2);
        }

      } else {
        // 比較対象データの両方がstring以外の型である場合
        result = (value1 < value2) ? -1 : (value1 > value2) ? 1 : 0;
      }

      return (event.order || 0) * result;
    });
  }

  /**
   * [一般開発者利用不可]
   * ウィンドウが子画面かどうかをチェックする。
   * (1) screenId 「CSB01003」についてはCTIツールバーから起動される(親画面情報が
   *     存在しない)子画面のため特殊なケースとして無条件でtrueを返却する。
   * (2) skippedScreenIds に記載されている画面IDは子画面判定の対象外 (QA:#59941)
   * @param screenId 画面ID (任意指定パラメータ)
   */
  isChildScreen(screenId?: string): boolean {
    if (typeof screenId !== 'undefined' && screenId === 'CSB01003') {
      return true;
    } else if (typeof screenId !== 'undefined' && this.skippedScreenIds.includes(screenId)) {
      this.logger.debug('子画面判定の対象外:' + screenId);
      return false;
    } else if (this.hasParentScreen || this.hasCaller()) { // ST不具合#68268の対応：this.hasCaller()のチェックを追加
      return true;
    } else {
      return false;
    }
  }

  /**
   * [一般開発者利用可]
   * 確認ダイアログのメッセージ内容を作成する。
   * @param id (0:画面間トランザクション実行中、1:非同期トランザクション実行中)
   */
  getConfirmationMessage(id: number): string {
    if (id === 0) {
      return '複数の画面間で更新中の処理があります。元に戻してもよろしいですか？';
    } else {
      return '現在、処理実行中です。当該処理は継続されますが、よろしいでしょうか？';
    }
  }

  /**
   * [一般開発者利用可]
   * 画面間のトランスアクションを開始
   * @param transactionKey 画面間のトランスアクションキー
   */
  beginMultiScreenTransaction(transactionKey: string) : void {
    if (transactionKey != null) {
      this.logger.debug('トランスアクション開始:' + transactionKey);
      this.transactionKey = transactionKey;
      this.dbTransactionFlag = true;
      // LocalStorageにtransactionKeyを追加する。
      let transactionKeyList: string[] | null = this.getItemFromLocalStorage('TransactionKeyList');
      if (transactionKeyList != null) {
        transactionKeyList?.push(transactionKey);
      } else {
        transactionKeyList = [transactionKey,];
      }
      this.setItemToLocalStorage('TransactionKeyList', [...transactionKeyList,])
    }
  }

  /**
   * [一般開発者利用可]
   * 画面間のトランスアクションを停止
   */
  stopMultiScreenTransaction() : void {
    this.logger.debug('トランスアクション停止!:' + this.transactionKey);
    // LocalStorageからtransactionKeyを削除する。
    const transactionKeyList: string[] | null = this.getItemFromLocalStorage('TransactionKeyList');
    if (transactionKeyList != null) {
      if (this.transactionKey != null) {
        transactionKeyList.splice(transactionKeyList.indexOf(this.transactionKey), 1);
        if (transactionKeyList.length !== 0) {
          this.setItemToLocalStorage('TransactionKeyList', [...transactionKeyList,])
        } else {
          this.removeItemFromLocalStorage('TransactionKeyList');
        }
      }
    } else {
      this.removeItemFromLocalStorage('TransactionKeyList');
    }
    this.dbTransactionFlag = false;
    this.transactionKey = null;
  }

  /**
   * [一般開発者利用可]
   * 画面にtransactionKeyを作成。
   */
  generateTransactionKey(userId?: string): string {
    return userId + '_' +
      this.screenInfo?.screenId + '_' +
      moment().format('YYYYMMDDHHmmssSSS') + '_' +
      Math.floor(10000 + Math.random() * 90000);
  }
}
